import { Button, ButtonPrimary } from '@/components/button'
import { People, Person, UserIconFilled } from '@/components/icons'
import { Search } from '@/components/search'
import { PlayerItemLoading } from '@/components/skeleton'
import { LIMIT, OFFSET } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { useDebounce } from '@/hooks/useDebounce'
import { useApproveMembers, useGetClubInvites, useRemoveMember } from '@/services'
import { ClubMember } from '@/types'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

const REQUESTS = 'REQUESTS'
const INVITED = 'INVITED'

export const ClubInvites = () => {
  const { t } = useTranslation()

  const params = useParams()
  const navigate = useNavigate()

  const { clubId } = params

  const { mutate: cancelInvite } = useRemoveMember()
  const { mutate: approveMembers } = useApproveMembers()

  const [playerSearchText, setMemberSearchText] = useState('')
  const [offset, setOffset] = useState(OFFSET)
  const [inviteTab, setInviteTab] = useState(REQUESTS)
  const [clubInvites, setClubInvites] = useState<Map<number, ClubMember>>(new Map())
  const [countRequests, setCountRequests] = useState(0)
  const [lastElement, setLastElement] = useState<any>(null)

  const debouncedPlayerSearchText = useDebounce<string>(playerSearchText || '*', 200)

  const {
    data: resClubInvites,
    isLoading,
    isSuccess: isSuccessGetClubInvites,
  } = useGetClubInvites({
    clubId: Number(clubId),
    limit: LIMIT,
    filter: {
      clubInvitationType: inviteTab === REQUESTS ? ['REQUEST'] : ['INVITATION', 'INVITATION_CSV'],
    },
    offset: offset,
    query: debouncedPlayerSearchText || '*',
  })

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const onChangeTab = (tab: string) => {
    setClubInvites(new Map())
    setInviteTab(tab)
    setOffset(OFFSET)
  }

  const onChangePlayerSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberSearchText(e.target.value)
    setOffset(OFFSET)
  }

  const onCancelInvite = (id: number) => {
    if (clubId) {
      cancelInvite(
        { clubId: Number(clubId), userIds: [id] },
        {
          onSuccess: () => {
            toast.success(t('common.removed'))
            setClubInvites((prev) => {
              const newClubInvites = new Map(prev)
              newClubInvites.delete(id)
              return newClubInvites
            })
            if (inviteTab === REQUESTS) {
              setCountRequests((prev) => prev - 1)
            }
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        }
      )
    }
  }

  const onApproveRequest = (id: number) => {
    if (clubId) {
      approveMembers(
        { clubId: Number(clubId), ids: [id] },
        {
          onSuccess: () => {
            toast.success(t('common.success'))
            setClubInvites((prev) => {
              const newClubInvites = new Map(prev)
              newClubInvites.delete(id)
              return newClubInvites
            })
            setCountRequests((prev) => prev - 1)
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        }
      )
    }
  }

  const onIgnoreAllInvite = () => {
    const ids = Array.from(clubInvites.values()).map((item) => item.id)
    if (clubId) {
      cancelInvite(
        { clubId: Number(clubId), userIds: ids },
        {
          onSuccess: () => {
            toast.success(t('common.removed'))
            setClubInvites(new Map())
            setCountRequests(0)
            setOffset(OFFSET)
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        }
      )
    }
  }

  const onApproveAllMembers = () => {
    const ids = Array.from(clubInvites.values()).map((item) => item.id)
    if (clubId) {
      approveMembers(
        { clubId: Number(clubId), ids: ids },
        {
          onSuccess: () => {
            toast.success(t('common.success'))
            setClubInvites(new Map())
            setCountRequests(0)
            setOffset(OFFSET)
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        }
      )
    }
  }

  const onNavigateProfile = (playerId: number) => navigate(`${ROUTE_PLAYER_PROFILE}/${playerId}`)

  useEffect(() => {
    if (resClubInvites && isSuccessGetClubInvites && inviteTab === REQUESTS) {
      if (offset === OFFSET) {
        setCountRequests(resClubInvites.result.total)
      } else {
        setCountRequests((prev) => prev + resClubInvites.result.hits.length)
      }
    }
  }, [resClubInvites, isSuccessGetClubInvites, inviteTab, offset])

  useEffect(() => {
    if (resClubInvites)
      setClubInvites((old) => {
        let temp: Map<number, ClubMember>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resClubInvites.result.hits.forEach((mem) => {
          temp.set(mem.id, mem)
        })

        return temp
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resClubInvites])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <h4 className="text-xl text-white font-semibold">{t('common.invites')}</h4>

        <Search
          searchText={playerSearchText}
          onChangeInput={onChangePlayerSearch}
          className="py-2 px-3 w-56 mt-3 gap-x-2"
        />

        <div className="flex flex-col sm:flex-row justify-between sm:items-center mt-4 mb-2 gap-2">
          <div className="flex gap-x-4 justify-start">
            <button className="pb-2 relative" onClick={() => onChangeTab(REQUESTS)}>
              <span className={`text-white font-semibold text-sm ${inviteTab === REQUESTS ? 'border-b-2 pb-2' : ''}`}>
                {t('common.requests')}
              </span>
              {!!countRequests && (
                <div className="absolute flex w-5 h-5 rounded-full bg-boldOrange -right-6 top-1 justify-center items-center">
                  <p className="text-xs font-medium text-white">{countRequests}</p>
                </div>
              )}
            </button>
            <button className="pb-2 ml-6" onClick={() => onChangeTab(INVITED)}>
              <span className={`text-white font-semibold text-sm ${inviteTab === INVITED ? 'border-b-2 pb-2' : ''}`}>
                {t('common.invited')}
              </span>
            </button>
          </div>
          <div className={`flex justify-between sm:justify-normal gap-x-2 h-9 ${inviteTab === INVITED && 'hidden'}`}>
            <Button
              className="py-2 px-4 text-sm font-semibold text-white hover:text-opacity-70"
              onClick={onIgnoreAllInvite}
              disabled={clubInvites.size === 0}
            >
              {t('common.ignoreAll')}
            </Button>
            <ButtonPrimary
              onClick={onApproveAllMembers}
              title={t('common.acceptAll')}
              disabled={clubInvites.size === 0}
            />
          </div>
        </div>
      </div>

      {Array.from(clubInvites.values())?.map((item, index) => {
        return index === clubInvites.size - 1 && resClubInvites?.result.hasMore ? (
          <div key={item.id} ref={setLastElement}>
            <div className="bg-white rounded-2xl p-4">
              <div className="flex justify-between">
                <div className="flex gap-x-2 cursor-pointer" onClick={() => onNavigateProfile(item.id)}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} className="rounded-full w-10 h-10" alt="avatar" />
                  ) : (
                    <UserIconFilled className="w-10 h-10 text-primary-500" />
                  )}
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-navy-900">{item?.fullName}</span>
                    <span className="text-sm text-navy-900 text-opacity-80">{item?.shortAddress}</span>
                    <span className="text-xs text-navy-900 text-opacity-60">
                      {item?.age}
                      {item?.gender ? ` • ${item?.gender[0]}` : ''}
                    </span>
                    <p className="text-xs text-boldOrange font-semibold">
                      {item?.invitationType === 'REQUEST' ? 'Requested:' : 'Invited:'}{' '}
                      {`${moment(item?.roles[0]?.created).fromNow()}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-x-4 items-center">
                  <div className="flex gap-x-1 items-center">
                    <div className="bg-navy-900 bg-opacity-10 rounded-lg px-2 py-1 w-11 h-10 flex flex-col justify-center items-center">
                      <People className="text-navy-900" />
                      <span className="text-xs text-navy-900 font-medium">{item?.doubles}</span>
                    </div>
                    <div className="bg-navy-900 bg-opacity-10 rounded-lg px-2 py-1 w-11 h-10 flex flex-col justify-center items-center">
                      <Person className="text-navy-900" />
                      <span className="text-xs text-navy-900 font-medium">{item?.singles}</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex gap-x-2 items-center">
                    {inviteTab === REQUESTS ? (
                      <>
                        <button
                          className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 text-sm"
                          onClick={() => onCancelInvite(item.id)}
                        >
                          {t('common.ignore')}
                        </button>
                        <ButtonPrimary title="Accept" onClick={() => onApproveRequest(item.id)} />
                      </>
                    ) : (
                      <button
                        className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 hover:bg-opacity-30 text-sm"
                        onClick={() => onCancelInvite(item.id)}
                      >
                        {t('common.cancelInvite')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="sm:hidden flex gap-x-2 items-center mt-3 justify-center">
                {inviteTab === REQUESTS ? (
                  <>
                    <button
                      className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 text-sm"
                      onClick={() => onCancelInvite(item.id)}
                    >
                      {t('common.ignore')}
                    </button>
                    <ButtonPrimary title="Accept" onClick={() => onApproveRequest(item.id)} />
                  </>
                ) : (
                  <button
                    className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 hover:bg-opacity-30 text-sm"
                    onClick={() => onCancelInvite(item.id)}
                  >
                    {t('common.cancelInvite')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-4" key={item.id}>
            <div className="flex justify-between">
              <div className="flex gap-x-2 cursor-pointer" onClick={() => onNavigateProfile(item.id)}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} className="rounded-full w-10 h-10" alt="avatar" />
                ) : (
                  <UserIconFilled className="w-10 h-10 text-primary-500" />
                )}
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-navy-900">{item?.fullName}</span>
                  <span className="text-sm text-navy-900 text-opacity-80">{item?.shortAddress}</span>
                  <span className="text-xs text-navy-900 text-opacity-60">
                    {item?.age}
                    {item?.gender ? ` • ${item?.gender[0]}` : ''}
                  </span>
                  <p className="text-xs text-boldOrange font-semibold">
                    {item?.invitationType === 'REQUEST' ? 'Requested:' : 'Invited:'}{' '}
                    {`${moment(item?.roles[0]?.created).fromNow()}`}
                  </p>
                </div>
              </div>
              <div className="flex gap-x-4 items-center">
                <div className="flex gap-x-1 items-center">
                  <div className="bg-navy-900 bg-opacity-10 rounded-lg px-2 py-1 w-11 h-10 flex flex-col justify-center items-center">
                    <People className="text-navy-900" />
                    <span className="text-xs text-navy-900 font-medium">{item?.doubles}</span>
                  </div>
                  <div className="bg-navy-900 bg-opacity-10 rounded-lg px-2 py-1 w-11 h-10 flex flex-col justify-center items-center">
                    <Person className="text-navy-900" />
                    <span className="text-xs text-navy-900 font-medium">{item?.singles}</span>
                  </div>
                </div>
                <div className="hidden sm:flex gap-x-2 items-center">
                  {inviteTab === REQUESTS ? (
                    <>
                      <button
                        className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 text-sm"
                        onClick={() => onCancelInvite(item.id)}
                      >
                        {t('common.ignore')}
                      </button>
                      <ButtonPrimary title="Accept" onClick={() => onApproveRequest(item.id)} />
                    </>
                  ) : (
                    <button
                      className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 hover:bg-opacity-30 text-sm"
                      onClick={() => onCancelInvite(item.id)}
                    >
                      {t('common.cancelInvite')}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="sm:hidden flex gap-x-2 items-center mt-3 justify-center">
              {inviteTab === REQUESTS ? (
                <>
                  <button
                    className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 text-sm"
                    onClick={() => onCancelInvite(item.id)}
                  >
                    {t('common.ignore')}
                  </button>
                  <ButtonPrimary title="Accept" onClick={() => onApproveRequest(item.id)} />
                </>
              ) : (
                <button
                  className="py-2 px-4 font-semibold text-navy-900 bg-navy-900 rounded-full bg-opacity-10 hover:bg-opacity-30 text-sm"
                  onClick={() => onCancelInvite(item.id)}
                >
                  {t('common.cancelInvite')}
                </button>
              )}
            </div>
          </div>
        )
      })}

      {isLoading && (
        <>
          <PlayerItemLoading className="!max-w-[964px]" />
          <PlayerItemLoading className="!max-w-[964px]" />
          <PlayerItemLoading className="!max-w-[964px]" />
          <PlayerItemLoading className="!max-w-[964px]" />
          <PlayerItemLoading className="!max-w-[964px]" />
          <PlayerItemLoading className="!max-w-[964px]" />
        </>
      )}
      {resClubInvites && !resClubInvites?.result?.hasMore && clubInvites.size === 0 && (
        <div className="flex justify-center">
          <p className="text-slate-300 text-base font-semibold">{t('common.noMoreResults')}</p>
        </div>
      )}
    </div>
  )
}
